import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import Edit from '@mui/icons-material/Edit';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  AllLogsTemplatesByTypeDocument,
  LogsTemplateDocument,
  QaqcSectionConfig,
  useUpdateLogsTemplateMutation,
} from '@/__generated__/graphql';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { FormHeader, LineBreak, tooltipStyles } from '../TemplateForm/styles';
import {
  assignQaqcSectionOrderFromArray,
  keyFromTitle,
  normalizeQaqcConfig,
  renumberQaqcSections,
  sortQaqcSectionsByOrder,
  useSnackBarMessage,
} from '../utils';
import { DraggableFieldKeyChips } from './DraggableFieldKeyChips';
import { SortableDragHandleIconButton, SortableQaqcShell } from './QaqcConfigDnd';
import { qaqcTemplateFields, selectedQaqcTemplate, selectedQaqcTemplateId } from '../state';

const readOnlyQaqcSettingChipSx = {
  backgroundColor: 'transparent',
  borderColor: abyssColors.primary[400],
  color: abyssColors.primary[600],
  '&.Mui-disabled': {
    opacity: 1,
    backgroundColor: 'transparent',
    borderColor: abyssColors.primary[300],
    color: abyssColors.primary[500],
    WebkitTextFillColor: abyssColors.primary[500],
  },
};

const uniqueKeyFromTitle = (title: string, reservedKeys: Iterable<string>) => {
  const used = new Set(reservedKeys);
  const base = keyFromTitle(title);
  const root = base.length > 0 ? base : 'key';
  if (!used.has(root)) {
    return root;
  }
  let index = 2;
  while (used.has(`${root}_${index}`)) {
    index += 1;
  }
  return `${root}_${index}`;
};

type QaqcConfigPanelProps = {
  structureId: string;
};

export const QaqcConfigPanel = ({ structureId }: QaqcConfigPanelProps) => {
  const templateId = useAtomValue(selectedQaqcTemplateId);
  const fields = useAtomValue(qaqcTemplateFields);
  const qaqcTemplate = useAtomValue(selectedQaqcTemplate);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [sections, setSections] = useState<QaqcSectionConfig[]>(() =>
    normalizeQaqcConfig(qaqcTemplate?.qaqcConfig ?? [])
  );
  const snapshotRef = useRef<QaqcSectionConfig[] | null>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'section' | 'table';
    sectionIndex: number;
    tableKey?: string;
  } | null>(null);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const fieldKeySet = useMemo(
    () => new Set((fields ?? []).map((f) => f.key).filter(Boolean) as string[]),
    [fields]
  );

  const fieldOptions = useMemo(
    () =>
      (fields ?? [])
        .filter((f) => f.key)
        .map((f) => ({ key: f.key as string, label: f.name ?? f.key ?? '' })),
    [fields]
  );

  useEffect(() => {
    if (modeRef.current === 'Edit') {
      return;
    }
    setSections(renumberQaqcSections(normalizeQaqcConfig(qaqcTemplate?.qaqcConfig ?? [])));
  }, [qaqcTemplate?.id, qaqcTemplate?.qaqcConfig]);

  const beginEdit = useCallback(() => {
    snapshotRef.current = structuredClone(sections);
    setMode('Edit');
  }, [sections]);

  const cancelEdit = useCallback(() => {
    if (snapshotRef.current) {
      setSections(snapshotRef.current);
    }
    snapshotRef.current = null;
    setMode('ReadOnly');
  }, []);

  const [updateTemplate, { loading: isSaving }] = useUpdateLogsTemplateMutation({
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar('Could not save QA/QC layout', 'error');
        return;
      }
      showSnackBar('QA/QC layout saved', 'success');
      snapshotRef.current = null;
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const validateAndSave = useCallback(() => {
    if (!templateId) {
      showSnackBar('Template id is not set', 'error');
      return;
    }

    const ordered = renumberQaqcSections(sections);
    const sectionKeys = new Set<string>();
    for (const section of ordered) {
      if (!section.sectionKey.trim() || !section.title.trim()) {
        showSnackBar('Each section needs a key and a title.', 'error');
        return;
      }
      if (sectionKeys.has(section.sectionKey.trim())) {
        showSnackBar(`Duplicate section key: ${section.sectionKey}`, 'error');
        return;
      }
      sectionKeys.add(section.sectionKey.trim());
      const tableKeys = new Set<string>();
      for (const table of section.tables) {
        if (!table.tableKey.trim() || !table.title.trim()) {
          showSnackBar(`Table in “${section.title}” needs a key and a title.`, 'error');
          return;
        }
        if (tableKeys.has(table.tableKey.trim())) {
          showSnackBar(
            `Duplicate table key in section “${section.title}”: ${table.tableKey}`,
            'error'
          );
          return;
        }
        tableKeys.add(table.tableKey.trim());
      }
    }

    updateTemplate({
      variables: {
        input: {
          structureId,
          templateId,
          qaqcConfig: ordered.map((section) => ({
            sectionKey: section.sectionKey.trim(),
            title: section.title.trim(),
            order: section.order,
            defaultOpen: section.defaultOpen,
            allowsMultipleSectionBlocks: section.allowsMultipleSectionBlocks,
            tables: section.tables.map((table) => ({
              tableKey: table.tableKey.trim(),
              title: table.title.trim(),
              fieldKeys: table.fieldKeys,
              order: table.order,
              supportsMultipleEntries: table.supportsMultipleEntries,
            })),
          })),
          lastModifiedFor: 'Update QA/QC layout',
        },
      },
      refetchQueries: [LogsTemplateDocument, AllLogsTemplatesByTypeDocument],
    });
  }, [sections, showSnackBar, structureId, templateId, updateTemplate]);

  const tableDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const moveSection = (index: number, direction: -1 | 1) => {
    setSections((previous) => {
      const sorted = sortQaqcSectionsByOrder(previous);
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= sorted.length) {
        return previous;
      }
      const moved = arrayMove(sorted, index, nextIndex);
      return assignQaqcSectionOrderFromArray(moved);
    });
  };

  const handleTableDragEnd = useCallback(
    (sectionIndex: number, sectionKey: string) => (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      setSections((previous) => {
        const list = previous.map((section) => ({ ...section, tables: [...section.tables] }));
        const tables = sortQaqcSectionsByOrder(list[sectionIndex].tables);
        const identifiers = tables.map((table) => `qaqc-table:${sectionKey}:${table.tableKey}`);
        const oldIndex = identifiers.indexOf(String(active.id));
        const newIndex = identifiers.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0) {
          return previous;
        }
        const reordered = arrayMove(tables, oldIndex, newIndex).map((table, index) => ({
          ...table,
          order: index + 1,
        }));
        list[sectionIndex].tables = reordered;
        return list;
      });
    },
    []
  );

  const addSection = () => {
    setSections((previous) => {
      const existingSectionKeys = previous.map((s) => s.sectionKey);
      const sectionKey = uniqueKeyFromTitle('New section', existingSectionKeys);
      const tableKey = uniqueKeyFromTitle('New table', []);
      return renumberQaqcSections([
        ...previous,
        {
          id: generateMongoObjectId(),
          sectionKey,
          title: 'New section',
          order: previous.length + 1,
          defaultOpen: true,
          allowsMultipleSectionBlocks: false,
          tables: [
            {
              id: generateMongoObjectId(),
              tableKey,
              title: 'New table',
              fieldKeys: [],
              order: 1,
              supportsMultipleEntries: false,
            },
          ],
        },
      ]);
    });
  };

  const addTable = (sectionIndex: number) => {
    setSections((previous) => {
      const list = previous.map((s, index) =>
        index === sectionIndex
          ? {
              ...s,
              tables: [
                ...s.tables,
                {
                  id: generateMongoObjectId(),
                  tableKey: uniqueKeyFromTitle(
                    'New table',
                    s.tables.map((t) => t.tableKey)
                  ),
                  title: 'New table',
                  fieldKeys: [],
                  order: s.tables.length + 1,
                  supportsMultipleEntries: false,
                },
              ],
            }
          : s
      );
      return renumberQaqcSections(list);
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) {
      return;
    }
    if (deleteTarget.type === 'section') {
      setSections((previous) =>
        renumberQaqcSections(previous.filter((_, index) => index !== deleteTarget.sectionIndex))
      );
    } else if (deleteTarget.tableKey) {
      setSections((previous) =>
        previous.map((section, sectionIndex) =>
          sectionIndex === deleteTarget.sectionIndex
            ? {
                ...section,
                tables: section.tables.filter((t) => t.tableKey !== deleteTarget.tableKey),
              }
            : section
        )
      );
    }
    setDeleteTarget(null);
  };

  const orderedSections = useMemo(() => renumberQaqcSections(sections), [sections]);

  const isEdit = useMemo(() => mode === 'Edit', [mode]);

  return (
    <Box sx={{ pt: 3, width: '100%' }}>
      <FormHeader
        sx={{
          alignItems: 'flex-start',
          gap: 2,
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize="2rem" fontWeight={600} color={abyssColors.primary[600]}>
            QA/QC form layout
          </Typography>
          <Typography
            variant="body2"
            color={abyssColors.primary[500]}
            component="div"
            sx={{
              mt: 1,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              backgroundColor: abyssColors.warning[50],
              border: `1px solid ${abyssColors.warning[200]}`,
              borderRadius: 1,
              p: 1.5,
              mb: 1.5,
            }}
          >
            <InfoOutlined sx={{ mt: '2px', flexShrink: 0 }} fontSize="small" />
            <span>
              Organize template fields into <strong>sections</strong> (e.g. Blast phase, Coating
              phase). <br />
              Each section contains one or more <strong>tables</strong> (e.g. Ambient Conditions,
              Blast Conditions). <br />
              Each table lists which <strong>fields</strong> appear together. This controls how the
              QA/QC logging screen is grouped.
            </span>
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, alignSelf: 'flex-start', pt: 0.5 }}>
          {isEdit ? (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => validateAndSave()}
                disabled={isSaving}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => cancelEdit()}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Tooltip title="Edit layout" arrow slotProps={{ popper: { sx: tooltipStyles } }}>
              <IconButton size="medium" onClick={() => beginEdit()}>
                <Edit sx={{ color: abyssColors.primary[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </FormHeader>

      <LineBreak marginBottom={2} />

      {isEdit && (
        <Button
          sx={{
            ...containedButtonStyle,
            fontSize: '1.2rem',
            py: 1,
            mb: 2,
            '&:hover': {
              background: abyssColors.primary[700],
            },
          }}
          startIcon={<Add />}
          variant="outlined"
          size="small"
          onClick={() => addSection()}
        >
          Add section
        </Button>
      )}

      {orderedSections.length === 0 ? (
        <Typography color={abyssColors.primary[500]} sx={{ mt: 0.5 }}>
          No sections are configured yet.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {orderedSections.map((section, sectionIndex) => (
            <Accordion
              key={section.sectionKey}
              defaultExpanded={section.defaultOpen ?? true}
              disableGutters
              sx={{
                border: `1px solid ${abyssColors.primary[200]}`,
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" spacing={1} width="100%" pr={1}>
                  <Typography fontWeight={600} sx={{ flex: 1 }}>
                    {section.order}. {section.title || section.sectionKey}
                  </Typography>
                  {section.allowsMultipleSectionBlocks && (
                    <Chip size="small" label="Multiple section/table blocks" variant="outlined" />
                  )}
                  {isEdit && (
                    <Stack direction="row" onClick={(event) => event.stopPropagation()}>
                      <IconButton
                        size="small"
                        aria-label="Move section up"
                        onClick={() => moveSection(sectionIndex, -1)}
                        disabled={sectionIndex === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Move section down"
                        onClick={() => moveSection(sectionIndex, 1)}
                        disabled={sectionIndex === orderedSections.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove section"
                        onClick={() => setDeleteTarget({ type: 'section', sectionIndex })}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  pt: 1,
                  px: 2,
                  pb: 2,
                }}
              >
                <Stack spacing={2}>
                  {isEdit ? (
                    <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap">
                      <TextField
                        label="Section Title"
                        value={section.title}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSections((previous) =>
                            previous.map((s, index) =>
                              index === sectionIndex ? { ...s, title: value } : s
                            )
                          );
                        }}
                        onBlur={() => {
                          setSections((previous) => {
                            const current = previous[sectionIndex];
                            if (!current) {
                              return previous;
                            }
                            const otherKeys = previous
                              .filter((_, index) => index !== sectionIndex)
                              .map((s) => s.sectionKey);
                            const nextKey = uniqueKeyFromTitle(current.title, otherKeys);
                            if (nextKey === current.sectionKey) {
                              return previous;
                            }
                            return previous.map((s, index) =>
                              index === sectionIndex ? { ...s, sectionKey: nextKey } : s
                            );
                          });
                        }}
                        size="small"
                        sx={{ minWidth: 220, flex: 1 }}
                      />
                      <TextField
                        label="Section Key"
                        value={section.sectionKey}
                        size="small"
                        sx={{ minWidth: 200, flex: 1 }}
                        disabled
                        helperText="Read-only key"
                      />
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      color={abyssColors.primary[500]}
                      sx={{ fontStyle: 'italic' }}
                    >
                      Section Key: {section.sectionKey}
                    </Typography>
                  )}

                  {isEdit ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={section.defaultOpen ?? false}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setSections((previous) =>
                                previous.map((s, index) =>
                                  index === sectionIndex ? { ...s, defaultOpen: checked } : s
                                )
                              );
                            }}
                          />
                        }
                        label="Start expanded"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={section.allowsMultipleSectionBlocks}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setSections((previous) =>
                                previous.map((s, index) =>
                                  index === sectionIndex
                                    ? { ...s, allowsMultipleSectionBlocks: checked }
                                    : s
                                )
                              );
                            }}
                          />
                        }
                        label="Allow multiple blocks/tables for this section"
                      />
                    </Stack>
                  ) : (
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Chip
                        variant="outlined"
                        size="small"
                        label={section.defaultOpen ? 'Expanded by default' : 'Collapsed by default'}
                        disabled
                        sx={readOnlyQaqcSettingChipSx}
                      />
                      <Chip
                        variant="outlined"
                        size="small"
                        label={
                          section.allowsMultipleSectionBlocks
                            ? 'Multiple section/table blocks allowed'
                            : 'Single section/table block'
                        }
                        disabled
                        sx={readOnlyQaqcSettingChipSx}
                      />
                    </Stack>
                  )}

                  <Typography variant="subtitle2" color={abyssColors.primary[500]}>
                    Tables in this section
                  </Typography>

                  <DndContext
                    sensors={tableDragSensors}
                    collisionDetection={closestCorners}
                    onDragEnd={handleTableDragEnd(sectionIndex, section.sectionKey)}
                  >
                    <SortableContext
                      items={sortQaqcSectionsByOrder(section.tables).map(
                        (table) => `qaqc-table:${section.sectionKey}:${table.tableKey}`
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      {sortQaqcSectionsByOrder(section.tables).map((table) => (
                        <SortableQaqcShell
                          key={table.tableKey}
                          id={`qaqc-table:${section.sectionKey}:${table.tableKey}`}
                          disabled={!isEdit}
                        >
                          {(tableHandleSlot) => (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                bgcolor: abyssColors.neutral[100],
                                border: `1px solid ${abyssColors.primary[200]}`,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                spacing={1}
                                sx={{ width: '100%' }}
                              >
                                {isEdit ? (
                                  <SortableDragHandleIconButton
                                    handleSlot={tableHandleSlot}
                                    ariaLabel="Drag to reorder table"
                                    sx={{ mt: -0.25 }}
                                  />
                                ) : null}
                                <Stack spacing={2} flex={1} minWidth={0} sx={{ width: '100%' }}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    gap={1}
                                  >
                                    <Typography fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                                      {table.order}. {table.title}
                                    </Typography>
                                    {isEdit && (
                                      <IconButton
                                        size="small"
                                        color="error"
                                        sx={{ flexShrink: 0 }}
                                        onClick={() =>
                                          setDeleteTarget({
                                            type: 'table',
                                            sectionIndex,
                                            tableKey: table.tableKey,
                                          })
                                        }
                                        aria-label="Remove table"
                                        disabled={section.tables.length <= 1}
                                      >
                                        <DeleteOutline fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Stack>

                                  {isEdit ? (
                                    <Stack spacing={2}>
                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField
                                          label="Table title"
                                          value={table.title}
                                          onChange={(event) => {
                                            const value = event.target.value;
                                            const tableKey = table.tableKey;
                                            setSections((previous) =>
                                              previous.map((s, sectionIndex_) =>
                                                sectionIndex_ === sectionIndex
                                                  ? {
                                                      ...s,
                                                      tables: s.tables.map((t) =>
                                                        t.tableKey === tableKey
                                                          ? { ...t, title: value }
                                                          : t
                                                      ),
                                                    }
                                                  : s
                                              )
                                            );
                                          }}
                                          onBlur={() => {
                                            const previousTableKey = table.tableKey;
                                            setSections((previous) => {
                                              const currentSection = previous[sectionIndex];
                                              if (!currentSection) {
                                                return previous;
                                              }
                                              const currentTable = currentSection.tables.find(
                                                (t) => t.tableKey === previousTableKey
                                              );
                                              if (!currentTable) {
                                                return previous;
                                              }
                                              const otherKeys = currentSection.tables
                                                .filter((t) => t.tableKey !== previousTableKey)
                                                .map((t) => t.tableKey);
                                              const nextKey = uniqueKeyFromTitle(
                                                currentTable.title,
                                                otherKeys
                                              );
                                              if (nextKey === previousTableKey) {
                                                return previous;
                                              }
                                              return previous.map((s, sectionIndex_) =>
                                                sectionIndex_ === sectionIndex
                                                  ? {
                                                      ...s,
                                                      tables: s.tables.map((t) =>
                                                        t.tableKey === previousTableKey
                                                          ? { ...t, tableKey: nextKey }
                                                          : t
                                                      ),
                                                    }
                                                  : s
                                              );
                                            });
                                          }}
                                          size="small"
                                          fullWidth
                                        />
                                        <TextField
                                          label="Table Key"
                                          value={table.tableKey}
                                          size="small"
                                          fullWidth
                                          disabled
                                          helperText="Read-only key"
                                        />
                                      </Stack>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={table.supportsMultipleEntries}
                                            onChange={(event) => {
                                              const checked = event.target.checked;
                                              const tableKey = table.tableKey;
                                              setSections((previous) =>
                                                previous.map((s, sectionIndex_) =>
                                                  sectionIndex_ === sectionIndex
                                                    ? {
                                                        ...s,
                                                        tables: s.tables.map((t) =>
                                                          t.tableKey === tableKey
                                                            ? {
                                                                ...t,
                                                                supportsMultipleEntries: checked,
                                                              }
                                                            : t
                                                        ),
                                                      }
                                                    : s
                                                )
                                              );
                                            }}
                                          />
                                        }
                                        label="Allow multiple entries for this table"
                                      />
                                      <Autocomplete
                                        multiple
                                        options={fieldOptions}
                                        getOptionLabel={(option) =>
                                          `${option.label} (${option.key})`
                                        }
                                        value={table.fieldKeys.map((key) => {
                                          const found = fieldOptions.find(
                                            (option) => option.key === key
                                          );
                                          return found ?? { key, label: key };
                                        })}
                                        onChange={(_event, value) => {
                                          const keys = value.map((v) => v.key);
                                          const tableKey = table.tableKey;
                                          setSections((previous) =>
                                            previous.map((s, sectionIndex_) =>
                                              sectionIndex_ === sectionIndex
                                                ? {
                                                    ...s,
                                                    tables: s.tables.map((t) =>
                                                      t.tableKey === tableKey
                                                        ? { ...t, fieldKeys: keys }
                                                        : t
                                                    ),
                                                  }
                                                : s
                                            )
                                          );
                                        }}
                                        disabled={fieldOptions.length === 0}
                                        renderInput={(parameters) => (
                                          <TextField
                                            {...parameters}
                                            label="Fields shown in this table"
                                            helperText={
                                              fieldOptions.length === 0
                                                ? 'Add template fields on the Fields tab first'
                                                : 'Pick fields in display order'
                                            }
                                          />
                                        )}
                                        renderTags={(value) => (
                                          <DraggableFieldKeyChips
                                            items={value.map((option) => ({
                                              key: option.key,
                                              label: option.label,
                                              unknown: !fieldKeySet.has(option.key),
                                            }))}
                                            disabled={mode === 'ReadOnly'}
                                            onReorder={(reorderedKeys) =>
                                              setSections((previous) =>
                                                previous.map((s, sectionIndex_) =>
                                                  sectionIndex_ === sectionIndex
                                                    ? {
                                                        ...s,
                                                        tables: s.tables.map((t) =>
                                                          t.tableKey === table.tableKey
                                                            ? {
                                                                ...t,
                                                                fieldKeys: reorderedKeys,
                                                              }
                                                            : t
                                                        ),
                                                      }
                                                    : s
                                                )
                                              )
                                            }
                                            onRemove={(key) =>
                                              setSections((previous) =>
                                                previous.map((s, sectionIndex_) =>
                                                  sectionIndex_ === sectionIndex
                                                    ? {
                                                        ...s,
                                                        tables: s.tables.map((t) =>
                                                          t.tableKey === table.tableKey
                                                            ? {
                                                                ...t,
                                                                fieldKeys: t.fieldKeys.filter(
                                                                  (k) => k !== key
                                                                ),
                                                              }
                                                            : t
                                                        ),
                                                      }
                                                    : s
                                                )
                                              )
                                            }
                                          />
                                        )}
                                      />
                                    </Stack>
                                  ) : (
                                    <Stack spacing={1}>
                                      <Stack
                                        direction="row"
                                        gap={1}
                                        flexWrap="wrap"
                                        alignItems="center"
                                      >
                                        <Typography
                                          variant="body2"
                                          color={abyssColors.primary[500]}
                                          sx={{ fontStyle: 'italic' }}
                                        >
                                          Table Key: {table.tableKey}
                                        </Typography>
                                        <Chip
                                          variant="outlined"
                                          size="small"
                                          label={
                                            table.supportsMultipleEntries
                                              ? 'Multiple entries'
                                              : 'Single entry'
                                          }
                                          disabled
                                          sx={readOnlyQaqcSettingChipSx}
                                        />
                                      </Stack>
                                      {table.fieldKeys.length === 0 ? (
                                        <Typography
                                          variant="body2"
                                          color={abyssColors.primary[500]}
                                        >
                                          No fields assigned
                                        </Typography>
                                      ) : (
                                        <DraggableFieldKeyChips
                                          items={table.fieldKeys.map((key) => ({
                                            key,
                                            label: fields?.find((f) => f.key === key)?.name ?? key,
                                            unknown: !fieldKeySet.has(key),
                                          }))}
                                          disabled
                                          onReorder={() => {}}
                                          onRemove={() => {}}
                                        />
                                      )}
                                    </Stack>
                                  )}
                                </Stack>
                              </Stack>
                            </Box>
                          )}
                        </SortableQaqcShell>
                      ))}
                    </SortableContext>
                  </DndContext>

                  {isEdit && (
                    <Button
                      sx={{
                        ...containedButtonStyle,
                        mt: 1,
                        mb: 2,
                        '&:hover': {
                          background: abyssColors.primary[700],
                        },
                      }}
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => addTable(sectionIndex)}
                    >
                      Add Table to This Section
                    </Button>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {deleteTarget && (
        <GenericDialog
          open
          onClose={() => setDeleteTarget(null)}
          title={deleteTarget.type === 'section' ? 'Remove section?' : 'Remove table?'}
          content={
            <Typography>
              {deleteTarget.type === 'section'
                ? 'This section and all its tables will be removed from the layout (not from saved data until you click Save).'
                : 'This table will be removed from the section.'}
            </Typography>
          }
          actions={
            <>
              <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="contained" onClick={() => confirmDelete()} sx={containedButtonStyle}>
                Remove
              </Button>
            </>
          }
          actionsStyles={{ mr: 2, pb: 2 }}
        />
      )}
    </Box>
  );
};
